import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Video, Link as LinkIcon, Upload, X, Film, Image as ImageIcon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const AddAnnouncementModal = ({ onClose, onSuccess }) => {
  const [type, setType] = useState('flyer');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    url: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const videoInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'];
    const allowedExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error('Formato não suportado. Use: MP4, MOV, AVI, WEBM ou MKV');
      return;
    }

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Arquivo muito grande. Máximo: 500MB');
      return;
    }

    setVideoFile(file);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error('Formato não suportado. Use: JPG, PNG, GIF ou WEBP');
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Arquivo muito grande. Máximo: 100MB');
      return;
    }

    setImageFile(file);
  };

  const removeVideo = () => {
    setVideoFile(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeImage = () => {
    setImageFile(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      if (type === 'video' && videoFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('title', formData.title);
        formDataUpload.append('file', videoFile);

        await axios.post(`${API}/announcements/video`, formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        
        toast.success('Vídeo publicado com sucesso!');
      } else if (type === 'flyer' && imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('title', formData.title);
        formDataUpload.append('content', formData.content || '');
        formDataUpload.append('file', imageFile);

        await axios.post(`${API}/announcements/flyer`, formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        
        toast.success('Flyer publicado com sucesso!');
      } else {
        // Para flyer sem imagem, usa tipo "texto" para evitar confusão
        const actualType = (type === 'flyer' && !imageFile) ? 'texto' : type;
        
        const payload = {
          type: actualType,
          title: formData.title,
          content: formData.content || null,
          url: formData.url || null
        };

        await axios.post(`${API}/announcements`, payload, getAuthHeaders());
        toast.success('Comunicado criado com sucesso!');
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar comunicado');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const isVideoUpload = type === 'video';
  const isFlyerUpload = type === 'flyer';
  const canSubmit = formData.title && (
    (type === 'flyer') ||
    (type === 'link' && formData.url) ||
    (type === 'video' && (videoFile || formData.url))
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="add-announcement-modal">
        <DialogHeader>
          <DialogTitle data-testid="add-announcement-title">Novo Comunicado</DialogTitle>
          <DialogDescription>Publique um comunicado para os parceiros</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-announcement-form">
          <div className="space-y-3">
            <Label>Tipo de Comunicado</Label>
            <RadioGroup value={type} onValueChange={(v) => { setType(v); setVideoFile(null); setImageFile(null); }} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="flyer" id="flyer" data-testid="radio-flyer" />
                <Label htmlFor="flyer" className="cursor-pointer flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Flyer/Texto
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="video" id="video" data-testid="radio-video" />
                <Label htmlFor="video" className="cursor-pointer flex items-center gap-1">
                  <Video className="w-4 h-4" />
                  Vídeo
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="link" id="link" data-testid="radio-link" />
                <Label htmlFor="link" className="cursor-pointer flex items-center gap-1">
                  <LinkIcon className="w-4 h-4" />
                  Link
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Título do comunicado"
              className="input-focus"
              data-testid="announcement-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Conteúdo (opcional)</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Descrição ou mensagem do comunicado..."
              className="input-focus min-h-[100px]"
              data-testid="announcement-content-input"
            />
          </div>

          {/* Flyer Image Upload */}
          {isFlyerUpload && (
            <div className="space-y-3">
              <Label>Imagem do Flyer (opcional)</Label>
              
              {!imageFile ? (
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => imageInputRef.current?.click()}
                  data-testid="image-upload-area"
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <ImageIcon className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Clique para selecionar uma imagem
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, GIF, WEBP • Máx. 100MB
                  </p>
                </div>
              ) : (
                <div className="border border-border rounded-lg p-4 bg-muted/30" data-testid="image-selected">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <ImageIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{imageFile.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(imageFile.size)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={removeImage}
                      className="shrink-0"
                      data-testid="remove-image-button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Video Upload */}
          {isVideoUpload && (
            <div className="space-y-3">
              <Label>Upload de Vídeo</Label>
              
              {!videoFile ? (
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => videoInputRef.current?.click()}
                  data-testid="video-upload-area"
                >
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,.mp4,.mov,.avi,.webm,.mkv"
                    onChange={handleVideoSelect}
                    className="hidden"
                    data-testid="video-file-input"
                  />
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Clique para selecionar um vídeo
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    MP4, MOV, AVI, WEBM, MKV • Máx. 500MB
                  </p>
                </div>
              ) : (
                <div className="border border-border rounded-lg p-4 bg-muted/30" data-testid="video-selected">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Film className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{videoFile.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(videoFile.size)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={removeVideo}
                      className="shrink-0"
                      data-testid="remove-video-button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-center text-xs text-muted-foreground">
                — ou —
              </div>

              <div className="space-y-2">
                <Label htmlFor="videoUrl">URL do Vídeo (YouTube, Vimeo, etc.)</Label>
                <Input
                  id="videoUrl"
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="input-focus"
                  disabled={!!videoFile}
                  data-testid="announcement-url-input"
                />
              </div>
            </div>
          )}

          {type === 'link' && (
            <div className="space-y-2">
              <Label htmlFor="url">URL do Link *</Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                placeholder="https://..."
                className="input-focus"
                data-testid="announcement-url-input"
              />
            </div>
          )}

          {loading && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Enviando arquivo...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1 btn-transition"
              data-testid="cancel-announcement-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !canSubmit}
              className="flex-1 btn-transition"
              data-testid="submit-announcement-button"
            >
              {loading ? (uploadProgress > 0 ? `Enviando ${uploadProgress}%` : 'Publicando...') : 'Publicar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAnnouncementModal;
